import React, { useState, useEffect, useCallback } from 'react'
import {
  Box,
  Flex,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs
} from '@chakra-ui/react'

import { StockData } from './types'
import { fetchStockData } from './utils'
import { useMSTRStyles } from './styles'

import LoadingState from './components/LoadingState'
import ErrorState from './components/ErrorState'
import StockHeader from './components/StockHeader'
import ChartPanel from './components/ChartPanel'
import MarketDataPanel from './components/MarketDataPanel'
import FinancialsPanel from './components/FinancialsPanel'
import KeyStatsPanel from './components/KeyStatsPanel'
import NewsPanel from './components/NewsPanel'

const MSTR: React.FC = () => {
  const [stockData, setStockData] = useState<StockData | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false)
  
  const { cardBg, statCardBg, gridTemplateColumns, borderColor } = useMSTRStyles()
  
  const loadData = useCallback(async () => {
    try {
      setIsLoading(true)
      setError(null)
      
      // Use a timeout to handle potential long-running requests
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error('Request timed out. Alpha Vantage API may be rate limited.')), 15000)
      })
      
      // Race between the actual data fetch and the timeout
      const data = await Promise.race([
        fetchStockData('MSTR'),
        timeoutPromise
      ])
      
      setStockData(data)
    } catch (err) {
      console.error('Error loading MSTR data:', err)
      setError(err instanceof Error ? err.message : 'Failed to fetch real-time stock data from Alpha Vantage')
      
      // Keep previous data if available during refresh errors
      if (isRefreshing && stockData) {
        // Just keep the previous data
      } else {
        setStockData(null)
      }
    } finally {
      setIsLoading(false)
      setIsRefreshing(false)
    }
  }, [isRefreshing, stockData])
  
  const handleRefresh = () => {
    setIsRefreshing(true)
    loadData()
  }
  
  useEffect(() => {
    loadData()
  }, [loadData])
  
  if (isLoading && !stockData) {
    return <LoadingState title="MSTR" subtitle="MicroStrategy" />
  }
  
  if (error && !stockData) {
    return (
      <ErrorState 
        error={error}
        isRetrying={isRefreshing}
        onRetry={handleRefresh}
        subtitle="MicroStrategy"
        title="MSTR"
      />
    )
  }
  
  return (
    <Box p={{ base: 2, md: 4 }}>
      {stockData && (
        <>
          <StockHeader 
            borderColor={borderColor}
            cardBg={cardBg}
            data={stockData}
            isRefreshing={isRefreshing}
            onRefresh={handleRefresh}
          />
          
          <Box mt={6}>
            <Tabs colorScheme="orange" variant="enclosed">
              <TabList>
                <Tab>Chart</Tab>
                <Tab>Market Data</Tab>
                <Tab>Financials</Tab>
                <Tab>News</Tab>
              </TabList>
              
              <TabPanels>
                <TabPanel>
                  <ChartPanel symbol="MSTR" />
                </TabPanel>
                
                <TabPanel>
                  <Flex direction="column" gap={6}>
                    <MarketDataPanel 
                      data={stockData} 
                      gridTemplateColumns={gridTemplateColumns}
                      statCardBg={statCardBg}
                    />
                    
                    <KeyStatsPanel 
                      data={stockData} 
                      gridTemplateColumns={gridTemplateColumns}
                      statCardBg={statCardBg}
                    />
                  </Flex>
                </TabPanel>
                
                <TabPanel>
                  <FinancialsPanel 
                    data={stockData} 
                    gridTemplateColumns={gridTemplateColumns}
                    statCardBg={statCardBg}
                  />
                </TabPanel>
                
                <TabPanel>
                  <NewsPanel 
                    data={stockData}
                    cardBg={cardBg}
                  />
                </TabPanel>
              </TabPanels>
            </Tabs>
          </Box>
        </>
      )}
    </Box>
  )
}

export default MSTR
